import { GUIDED_MODE_SCOPES } from "../shared/TourEngine/TourEngine";
import { getFeatureById } from "./featureRegistry";
import { GUIDE_TARGETS } from "./targets";

const DEFAULT_ENTRY_SCREEN = "kpi_dashboard";

const MENU_OPEN_EVENTS = new Set(["quick-menu-opened", "mission-control-opened"]);

/** Navigation Overhaul tour step order — navigation first, voice last. */
const NAV_OVERHAUL_TOUR_ORDER = Object.freeze([
  "ashley-tap-voice",
  "ashley-stop-listening",
  "ashley-hold-quick-menu",
  "quick-menu-all-modules",
  "unified-back-nav",
  "ashley-voice-commands",
]);

const NAV_OVERHAUL_COMPLETION = Object.freeze({
  title: "You're all set!",
  description:
    "You now understand CrewPilot's new navigation — how to move around, find every module, and go back. Finish to unlock your dashboard.",
});

/**
 * Release-layer step extensions keyed by feature id.
 * Keeps domain mapping out of the Tour Engine.
 */
const STEP_EXTENSIONS_BY_FEATURE_ID = Object.freeze({
  "ashley-hold-quick-menu": {
    targetTransition: {
      selector: GUIDE_TARGETS.QUICK_MENU,
      title: "Quick Menu",
      description:
        "This is your Quick Menu — shortcuts to the modules you use most. Press Next when you are ready to continue.",
      placement: "top",
      interaction: { type: "information", mode: "next" },
      timeoutMs: 12000,
    },
    guidedModeScopes: [GUIDED_MODE_SCOPES.SUSPEND_AUTO_DISMISS],
  },
  "quick-menu-all-modules": {
    hideTooltip: true,
    guidedModeScopes: [GUIDED_MODE_SCOPES.SUSPEND_AUTO_DISMISS],
  },
  "unified-back-nav": {
    guidedModeScopes: [GUIDED_MODE_SCOPES.SUSPEND_AUTO_DISMISS],
  },
});

/**
 * Maps Product Education feature definitions to generic TourEngine steps.
 * Domain knowledge stays here — not in the engine.
 *
 * @param {string[]} featureIds
 * @param {Object} [options]
 * @param {string} [options.entryScreen]
 * @param {Object} [options.releaseMeta]
 * @returns {import('../shared/TourEngine/types').TourStep[]}
 */
export function buildTourStepsFromFeatures(featureIds, options = {}) {
  const { entryScreen = DEFAULT_ENTRY_SCREEN, releaseMeta = null } = options;
  const orderedFeatureIds = orderTourFeatureIds(featureIds, releaseMeta?.releaseId);
  const steps = [];

  for (const featureId of orderedFeatureIds) {
    const feature = getFeatureById(featureId);
    if (!feature || feature.tour?.include === false) {
      continue;
    }

    steps.push(mapFeatureToStep(feature, { entryScreen }));
  }

  if (releaseMeta) {
    const completionCopy =
      releaseMeta.releaseId === "2026.06-nav-overhaul"
        ? NAV_OVERHAUL_COMPLETION
        : {
            title: releaseMeta.title
              ? `You're ready to use ${releaseMeta.title.toLowerCase()}`
              : "You're ready to continue",
            description:
              releaseMeta.description ||
              "Finish to return to CrewPilot with the dashboard unlocked.",
          };

    steps.push({
      id: `${releaseMeta.releaseId || "release"}-completion`,
      kind: "completion",
      title: completionCopy.title,
      description: completionCopy.description,
      targetSelector: null,
      interaction: null,
    });
  }

  return steps;
}

export function orderTourFeatureIds(featureIds, releaseId) {
  if (releaseId !== "2026.06-nav-overhaul") {
    return featureIds;
  }

  const ordered = NAV_OVERHAUL_TOUR_ORDER.filter((id) => featureIds.includes(id));
  const remainder = featureIds.filter((id) => !NAV_OVERHAUL_TOUR_ORDER.includes(id));
  return [...ordered, ...remainder];
}

function mapFeatureToStep(feature, { entryScreen }) {
  const interaction = resolveFeatureInteraction(feature, { entryScreen });
  const extensions = STEP_EXTENSIONS_BY_FEATURE_ID[feature.id] || {};

  return {
    id: feature.id,
    kind: "step",
    title: feature.title,
    description: feature.tourBody || feature.description,
    targetSelector: feature.target?.selector || null,
    secondaryTargetSelectors: resolveSecondaryTargetSelectors(feature, extensions),
    secondaryTargetTimeoutMs: resolveSecondaryTargetTimeout(feature, extensions),
    placement: feature.tour?.placement || "auto",
    scrollIntoView: Boolean(feature.target?.scrollIntoView),
    interaction,
    targetTransition: extensions.targetTransition || feature.tour?.targetTransition || null,
    guidedModeScopes: extensions.guidedModeScopes || feature.tour?.guidedModeScopes || [],
    hideTooltip: extensions.hideTooltip ?? feature.tour?.hideTooltip ?? false,
    refocusPrimaryOnHoldStart: extensions.refocusPrimaryOnHoldStart ?? false,
  };
}

function resolveSecondaryTargetSelectors(feature, extensions) {
  const selectors = [
    ...(extensions.secondaryTargetSelectors || []),
    ...extractSecondaryTargetSelectors(feature),
  ];

  return [...new Set(selectors.filter(Boolean))];
}

function resolveSecondaryTargetTimeout(feature, extensions) {
  return (
    extensions.secondaryTargetTimeoutMs ??
    feature.tour?.secondaryTargetTimeoutMs ??
    feature.secondaryTargetTimeoutMs ??
    undefined
  );
}

function extractSecondaryTargetSelectors(feature) {
  const sources = [feature.secondaryTargets, feature.tour?.secondaryTargets];
  const selectors = [];

  for (const source of sources) {
    if (!Array.isArray(source)) {
      continue;
    }

    for (const entry of source) {
      if (typeof entry === "string") {
        selectors.push(entry);
      } else if (entry?.selector) {
        selectors.push(entry.selector);
      }
    }
  }

  return selectors;
}

function resolveFeatureInteraction(feature, { entryScreen }) {
  const { interaction, target } = feature;
  if (!interaction) {
    return { type: "information", mode: "next" };
  }

  if (
    interaction.advanceOn === "next" &&
    !(target?.selector && target?.screen && target.screen !== entryScreen)
  ) {
    return { type: "information", mode: "next" };
  }

  if (interaction.advanceOn === "click_target") {
    return { type: "interaction", mode: "click_target" };
  }

  if (interaction.advanceOn === "longpress_target") {
    return { type: "interaction", mode: "longpress_target" };
  }

  if (interaction.advanceOn === "custom_event" && interaction.eventName) {
    const isAshleyTarget = target?.selector?.includes("ashley");
    if (
      target?.selector &&
      MENU_OPEN_EVENTS.has(interaction.eventName) &&
      !isAshleyTarget
    ) {
      return { type: "interaction", mode: "click_target" };
    }

    return {
      type: "interaction",
      mode: "custom_event",
      eventName: interaction.eventName,
    };
  }

  if (target?.selector && target?.screen && target.screen !== entryScreen) {
    return { type: "interaction", mode: "click_target" };
  }

  if (interaction.requiresUserAction && target?.selector) {
    return { type: "interaction", mode: "click_target" };
  }

  return { type: "information", mode: "next" };
}
